import React from 'react';
import postgresql from '../assets/logos/postgresql.svg';
import redis from '../assets/logos/redis.svg';
import mongodb from '../assets/logos/mongodb.svg';
import elasticsearch from '../assets/logos/elasticsearch.svg';
import rabbitmq from '../assets/logos/rabbitmq.svg';
import minio from '../assets/logos/minio.svg';
import erpnext from '../assets/logos/erpnext.svg';

/**
 * Vendor logo for a catalog entry.
 *
 * Files are committed under src/assets/logos and bundled, NOT fetched at runtime. That is
 * required, not a preference: SarvKube is meant to run air-gapped on-prem, where a remote
 * image URL would render as a broken icon.
 *
 * Keys match the catalog's `key`, with aliases because the same engine appears twice — the
 * ported script entry and the generic Helm one (`postgresql-ha` and `postgresql`).
 *
 * Unknown keys fall back to a lettermark rather than an empty box, so adding a catalog entry
 * never leaves a hole in the grid.
 */
const LOGOS = {
  postgresql,
  'postgresql-ha': postgresql,
  redis,
  mongodb,
  elasticsearch,
  elastic: elasticsearch,
  rabbitmq,
  minio,
  erpnext,
};

// Brand colours, used for the fallback tile and the soft background behind each logo.
const TINTS = {
  postgresql: '#336791',
  'postgresql-ha': '#336791',
  redis: '#DC382D',
  mongodb: '#00ED64',
  elasticsearch: '#FEC514',
  elastic: '#FEC514',
  rabbitmq: '#FF6600',
  minio: '#C72E49',
  erpnext: '#0089FF',
};

function initials(key = '') {
  const clean = key.replace(/-ha$/, '');
  return clean.slice(0, 2).toUpperCase();
}

export default function AppLogo({ appKey, size = 40, showTint = true }) {
  const src = LOGOS[appKey];
  const tint = TINTS[appKey] || '#64748b';

  const box = {
    width: size,
    height: size,
    borderRadius: Math.round(size * 0.22),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    // A faint wash of the brand colour so a white-on-transparent logo (Redis, Elastic) still
    // reads against a light card.
    background: showTint ? `${tint}14` : 'transparent',
    border: showTint ? `1px solid ${tint}33` : 'none',
    overflow: 'hidden',
  };

  if (!src) {
    return (
      <div style={{ ...box, background: tint, border: 'none' }} aria-label={appKey}>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: Math.round(size * 0.34),
                       letterSpacing: 0.5 }}>
          {initials(appKey)}
        </span>
      </div>
    );
  }

  return (
    <div style={box}>
      <img
        src={src}
        alt={appKey}
        // contain, not cover: several of these are wide wordmarks (Redis, RabbitMQ,
        // Elasticsearch are ~6:1) and cropping them to a square would cut the name off.
        style={{ width: '78%', height: '78%', objectFit: 'contain', display: 'block' }}
      />
    </div>
  );
}
